import { useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { useActionState, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  QUERY_KEY_NOVELTIES_BEST_CANDIDATES,
  QUERY_KEY_SKU_FORECAST_DATA,
} from 'src/keys';
import { useUpsertProductsSalesPotential } from 'src/services/api/upsertProductsSalesPotential';

type UseSalesPotentialEditorParams = {
  sku: string;
  salesPotential: number | null;
};

export function useSalesPotentialEditor({
  sku,
  salesPotential,
}: UseSalesPotentialEditorParams) {
  const { t } = useTranslation('novelties');
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(salesPotential?.toString() ?? '');

  const { mutateAsync: upsertSalesPotential, isPending: isMutationPending } =
    useUpsertProductsSalesPotential();

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(salesPotential?.toString() ?? '');
  };

  const [, saveSalesPotentialAction, isActionPending] = useActionState(
    async (_state: null, formData: FormData) => {
      const rawValue = formData.get('salesPotential');
      const value = Number.parseFloat(String(rawValue ?? ''));

      if (Number.isNaN(value) || value < 0) {
        toast.error(t('Error'), {
          description: t('Please enter a valid positive number'),
        });
        return null;
      }

      try {
        await upsertSalesPotential({ sku, sales_potential: value });
        setIsEditing(false);
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_NOVELTIES_BEST_CANDIDATES],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_SKU_FORECAST_DATA, sku],
        });
        toast.success(t('Success'), {
          description: t('Sales potential updated successfully'),
        });
      } catch {
        toast.error(t('Error'), {
          description: t('Failed to update sales potential'),
        });
      }

      return null;
    },
    null,
  );

  const isPending = isMutationPending || isActionPending;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(salesPotential?.toString() ?? '');
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCancel();
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();

    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return {
    isEditing,
    editValue,
    isPending,
    saveSalesPotentialAction,
    setEditValue,
    handleEdit,
    handleCancelClick,
    handleInputClick,
    handleKeyDown,
  };
}
